import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  Tooltip
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import GirviPageShell from '../components/common/GirviPageShell';
import baseURL from '../baseURL';

const STATUS_COLOR = {
  complete: 'success',
  in_progress: 'info',
  partial: 'warning',
  pending: 'default',
  failed: 'error'
};

function fmtTime(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function fmtNum(n) {
  if (n == null) return '—';
  return Number(n).toLocaleString('en-IN');
}

const ImportStatus = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${baseURL}/import-status`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      const json = await res.json();
      if (!res.ok || json.success === false) {
        throw new Error(json.message || `HTTP ${res.status}`);
      }
      setData(json.data);
      setError('');
    } catch (e) {
      setError(e.message || 'Failed to load import status');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const hasRunning = (data?.activeJobs?.length || 0) > 0;
    const intervalMs = hasRunning ? 5000 : 30000;
    const id = setInterval(load, intervalMs);
    return () => clearInterval(id);
  }, [data?.activeJobs?.length, load]);

  return (
    <GirviPageShell
      title="Import Status"
      subtitle="Master · Import"
      chipLabel="Master"
      maxWidth="xl"
      loading={loading && !data}
      compact
      actions={
        <Tooltip title="Refresh now">
          <IconButton
            onClick={load}
            disabled={loading}
            sx={{ color: '#f5e6c8' }}
          >
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      }
    >
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {data && (
        <>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
            Last updated: {fmtTime(data.generatedAt)}
            {(data.activeJobs?.length || 0) > 0 ? ' · auto-refresh every 5s' : ' · auto-refresh every 30s'}
          </Typography>

          {/* DB snapshot */}
          <Paper sx={{ p: 2, mb: 3 }} elevation={0} variant="outlined">
            <Typography variant="subtitle1" gutterBottom fontWeight={600}>
              Database (VPS A)
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              <Chip label={`Villages: ${fmtNum(data.counts.villages)}`} />
              <Chip label={`Customers: ${fmtNum(data.counts.customers)}`} color="primary" variant="outlined" />
              <Chip label={`Pledges (active): ${fmtNum(data.counts.pledgesActive)}`} />
              <Chip label={`Pledges (total): ${fmtNum(data.counts.pledgesTotal)}`} variant="outlined" />
            </Box>
          </Paper>

          {/* Active jobs */}
          {(data.activeJobs?.length || 0) > 0 && (
            <Paper sx={{ p: 2, mb: 3, borderLeft: 4, borderColor: 'info.main' }} elevation={0} variant="outlined">
              <Typography variant="subtitle1" gutterBottom fontWeight={600}>
                Running now
              </Typography>
              {data.activeJobs.map((job) => (
                <Box key={job.id} sx={{ mb: 2 }}>
                  <Typography variant="body2" fontWeight={600}>
                    {job.label}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {job.sourceFile || job.jobKey} · started {fmtTime(job.startedAt)}
                  </Typography>
                  {job.totalRows > 0 && (
                    <>
                      <LinearProgress
                        variant="determinate"
                        value={job.progressPct || 0}
                        sx={{ mt: 1, mb: 0.5, height: 8, borderRadius: 1 }}
                      />
                      <Typography variant="caption">
                        {fmtNum(job.processed)} / {fmtNum(job.totalRows)} rows
                        {job.progressPct != null ? ` (${job.progressPct}%)` : ''}
                        {' · '}created {fmtNum(job.created)} · skipped {fmtNum(job.skipped)} · errors{' '}
                        {fmtNum(job.errors)}
                      </Typography>
                    </>
                  )}
                  {job.message && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      {job.message}
                    </Typography>
                  )}
                </Box>
              ))}
            </Paper>
          )}

          {/* Pipeline */}
          <Paper sx={{ p: 2, mb: 3 }} elevation={0} variant="outlined">
            <Typography variant="subtitle1" gutterBottom fontWeight={600}>
              Import pipeline
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Step</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">In DB</TableCell>
                    <TableCell align="right">Expected (file)</TableCell>
                    <TableCell>Progress</TableCell>
                    <TableCell>Source file</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.pipeline.map((step) => (
                    <TableRow key={step.key} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {step.title}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" component="div">
                          {step.npmCommand}
                        </Typography>
                        {step.note && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            {step.note}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={step.statusLabel}
                          color={STATUS_COLOR[step.status] || 'default'}
                        />
                      </TableCell>
                      <TableCell align="right">
                        {step.matchedInDb != null ? fmtNum(step.matchedInDb) : fmtNum(step.inDb)}
                        {step.matchedInDb != null && step.key === 'common-entries' && (
                          <Typography variant="caption" display="block" color="text.secondary">
                            of {fmtNum(step.inDb)} total pledges
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {step.source?.exists ? fmtNum(step.expected) : '—'}
                      </TableCell>
                      <TableCell sx={{ minWidth: 120 }}>
                        {step.expected > 0 ? (
                          <>
                            <LinearProgress
                              variant="determinate"
                              value={step.progressPct}
                              color={step.status === 'complete' ? 'success' : 'primary'}
                              sx={{ height: 6, borderRadius: 1 }}
                            />
                            <Typography variant="caption">{step.progressPct}%</Typography>
                          </>
                        ) : (
                          '—'
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" display="block">
                          {step.source?.path || '—'}
                        </Typography>
                        {step.source?.exists ? (
                          <Typography variant="caption" color="text.secondary">
                            modified {fmtTime(step.source.modifiedAt)}
                          </Typography>
                        ) : (
                          <Typography variant="caption" color="error">
                            file missing
                          </Typography>
                        )}
                        {step.log?.lastRunAt && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            Last log: {step.log.lastRunAt}
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          {/* Recent jobs */}
          {(data.recentJobs?.length || 0) > 0 && (
            <Paper sx={{ p: 2 }} elevation={0} variant="outlined">
              <Typography variant="subtitle1" gutterBottom fontWeight={600}>
                Recent import runs
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>When</TableCell>
                      <TableCell>Job</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Created</TableCell>
                      <TableCell align="right">Skipped</TableCell>
                      <TableCell align="right">Errors</TableCell>
                      <TableCell>Message</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.recentJobs.map((job) => (
                      <TableRow key={job.id}>
                        <TableCell>{fmtTime(job.finishedAt || job.startedAt || job.createdAt)}</TableCell>
                        <TableCell>{job.label}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={job.status}
                            color={
                              job.status === 'completed'
                                ? 'success'
                                : job.status === 'failed'
                                  ? 'error'
                                  : job.status === 'running'
                                    ? 'info'
                                    : 'default'
                            }
                          />
                        </TableCell>
                        <TableCell align="right">{fmtNum(job.created)}</TableCell>
                        <TableCell align="right">{fmtNum(job.skipped)}</TableCell>
                        <TableCell align="right">{fmtNum(job.errors)}</TableCell>
                        <TableCell>
                          <Typography variant="caption">{job.message || '—'}</Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}
        </>
      )}
    </GirviPageShell>
  );
};

export default ImportStatus;
